// Shared formatters + table style tokens for the superadmin AI-usage panels.
// Consolidated here so a currency/format tweak (or a table restyle) is a
// one-file change instead of drifting across every AiUsage* component.

import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';

import type { AppRouter } from '@poseidon/api/types';

/** Dollars without the `$` (and with thousands separators) — for when a $ icon already carries the unit. */
export const usdAmount = (n: number): string =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const usd = (n: number): string => `$${usdAmount(n)}`;
export const int = (n: number): string => Math.round(n).toLocaleString();

export const CELL = 'px-3 py-2 text-sm';
export const HEAD = 'px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-400';
// Alternating rows: every second row tinted. The tint is TRANSLUCENT, not a flat
// color: this page's panels sit on a light-gray surface, where a flat bg-slate-50
// stripe (the Sequences table's, on white) vanished. A tint darkens whatever it's
// on. CSS `even:` — no index plumbing.
export const ROW = 'border-t border-gray-100 even:bg-slate-500/[0.07] dark:border-gray-800 dark:even:bg-white/[0.03]';

/** The dashboard's reporting window — 1 day, 1 week, or a UTC calendar month. */
export type AiUsagePeriod = NonNullable<inferRouterInputs<AppRouter>['aiUsage']['summary']['period']>;

// Keyed by the server's union, so it can't miss (or invent) a period: the one list
// the date-range radios and the URL schema check against.
const AI_USAGE_PERIOD_SET: Record<AiUsagePeriod, true> = {
  day: true,
  week: true,
  thisMonth: true,
  lastMonth: true,
  custom: true,
};
export const isAiUsagePeriod = (v: string): v is AiUsagePeriod => Object.hasOwn(AI_USAGE_PERIOD_SET, v);

/**
 * Month name `offset` months from now, on the UTC clock the server's month
 * windows use — so the "August" button always queries August's data, even in the
 * hours a US time zone is still in the previous month.
 */
export function utcMonthName(offset: number, now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  return d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
}

// The provider union, via the price book's output (its rows carry `AiProvider`).
type ProviderKey = inferRouterOutputs<AppRouter>['aiUsage']['prices'][number]['provider'];

// One FIXED color + display label per provider, shared by the cost chart and the
// Cost Explorer. A provider's color is keyed by its name — never by rank, filter or
// period — so it stays put everywhere, in both themes. Typed by the inferred
// provider union so adding a provider on the server forces an entry here.
//
// Hues are the Okabe–Ito colorblind-safe palette (Okabe & Ito 2008; Wong, Nature
// Methods 2011), run through the dataviz palette validator. The four providers with
// real spend take its recommended ≤4-category set (orange, sky blue, blue,
// vermilion), which validates all-pairs for CVD — needed because a team row's
// stacked bar can put any two side by side. The pairs that share rows most got
// the most separated combos: Tavily↔Anthropic (sky↔vermilion) and Wiza↔Twilio
// (blue↔orange). The set's closest pair, vermilion↔orange, falls on
// Anthropic↔Twilio, which only meet in team/user rows and where Twilio is tiny.
// Rarely-seen Exa and OpenAI take the palette's remaining bluish green and reddish
// purple. Declared alphabetically: this is also the legends' fixed order.
const PROVIDER_META: Record<ProviderKey, { label: string; color: string }> = {
  anthropic: { label: 'Anthropic', color: '#D55E00' }, // vermilion
  exa: { label: 'Exa', color: '#009E73' }, // bluish green
  openai: { label: 'OpenAI', color: '#CC79A7' }, // reddish purple
  tavily: { label: 'Tavily', color: '#56B4E9' }, // sky blue
  'twilio-lookup': { label: 'Twilio Lookup', color: '#E69F00' }, // orange
  wiza: { label: 'Wiza', color: '#0072B2' }, // blue
};

const PROVIDER_ORDER = Object.keys(PROVIDER_META);

// `event.provider` is a free-text column, so a stray/legacy value outside the
// union can reach the client — render it neutral gray, labeled by its raw key.
const UNKNOWN_PROVIDER_COLOR = '#94a3b8';

const isKnownProvider = (provider: string): provider is ProviderKey =>
  Object.prototype.hasOwnProperty.call(PROVIDER_META, provider);

export function providerMeta(provider: string): { label: string; color: string } {
  return isKnownProvider(provider) ? PROVIDER_META[provider] : { label: provider, color: UNKNOWN_PROVIDER_COLOR };
}

/**
 * Sort comparator for legends: the fixed declaration order above, unknown providers
 * last (alphabetically) — so a legend never reshuffles as spend or filters change.
 */
export function compareProviders(a: string, b: string): number {
  const ia = PROVIDER_ORDER.indexOf(a);
  const ib = PROVIDER_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
}
